import React, { useEffect, useRef, useState } from "react";
import { ErrorMessage, Form, Formik, FormikHelpers } from "formik";
import { Button, Col, Row } from "react-bootstrap";
import {
    ModalDelete,
    PhonePicker,
    ReactAsyncSelect,
    ReactSelect,
    Spinner,
    TextInput,
    SpinnerLoader,
    WithModulesAccess,
    MultiSelect,
} from "@services/ui-components";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { ApolloError } from "@apollo/client";
import {
    countryOptionsList,
    currencyOptionsList,
    timezoneOptionsList,
    updateCompanyFormValidationSchema,
} from "./constants";
import ErrorService from "../../services/ErrorService";
import { ErrorModalComponent } from "@me-pos/error-modal";
import i18next from "i18next";
import ImageCrop from "@services/ui-components/src/ImageCrop/ImageCrop";
import { AllModulesTagName } from "../../utils/enums";
import { PhoneInputRefType } from "react-international-phone";
import {
    useGetGmtForUpdateCompanyQuery,
    useGetInfoCompanyForUpdateCompanyPageQuery,
    useGetLanguagesForLanguageVersionsQuery,
    useGetRelationsQuery,
    useUpdateCompanyMutation,
} from "@me-team/host/main/company/graphql/company.hooks";
import { CompanyInput } from "@me-team/host/main/graphql/types";
import LanguagesService from "../../services/LanguagesService/LanguagesService";
import { currencySingVar } from "@me-team/host/main/globalVar/state";
import { ValidationErrorExtensions } from "@me-team/host/src/utils/type";
import { MultiOption } from "../../../../../services/ui-components/src/MultiSelect/MultiSelect";
import { getLanguageVersionOptions } from "../Me-menu/AllMenusPage/options";
import { useModulesAccess } from "@me-team/host/src/utils/useModulesAccess";
import ScrollToErrorHelper from "../../services/ScrollToErrorHelper/ScrollToErrorHelper";
import { CompanyEditNavState } from "../../interfaces/meMenuInterfaces/interfaces";
import { BUSINESS_TYPE } from "../BusinessTypeSelector/businessType";
import { parseBusinessType } from "../BusinessTypeSelector/parseBusinessType";
import { resetMenuLanguageVersionOnPrimaryChange } from "../../utils/menuLanguageVersion/resetMenuLanguageVersionOnPrimaryChange";
import { useCountryCitiesPaginatedOptions } from "../../hooks/useCountryCitiesPaginatedOptions";
import { CUSTOM_CITY_OPTION_VALUE } from "../../constants/city";

type SetFieldValueType = (field: string, value: string | boolean | string[] | File | (string | File)[]) => void;

interface Option {
    value: any;
    label: string;
}

interface UpdateCompanyProps {
    setShowModalDelete: (showModalDelete: boolean) => void;
    showModalDelete: boolean;
    canViewCompanyInfo: boolean;
    canEditCompanyInfo: boolean;
    canDeleteRestoreCompany: boolean;
}

const UpdateCompanyForm: React.FC<UpdateCompanyProps> = ({
    setShowModalDelete,
    showModalDelete,
    canViewCompanyInfo,
    canEditCompanyInfo,
    canDeleteRestoreCompany,
}) => {
    const { t } = useTranslation();
    const { branchId, currentCompanyId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const navState: CompanyEditNavState = location.state ?? {};
    const returnTo = navState.flow === "add_menu_language" && navState.from ? navState.from : null;
    const phonePickerRef = useRef<PhoneInputRefType>(null);
    const [isErrorModalOpen, setIsErrorModalOpen] = useState<number>(null);
    const [selectedCountry, setSelectedCountry] = useState(null);
    const [selectedCity, setSelectedCity] = useState(null);
    const [showRecoveryModal, setShowRecoveryModal] = useState(false);
    const [initialValues, setInitialValues] = useState<CompanyInput | null>(null);

    const { data: timezoneData } = useGetGmtForUpdateCompanyQuery({
        variables: { countryId: selectedCountry?.value },
        skip: !selectedCountry?.value,
    });

    const enabledMenuModule: boolean = useModulesAccess([AllModulesTagName.MENU]);

    const handleRecoveryModalClose = () => setShowRecoveryModal(false);

    const isOtherCity = selectedCity?.value === CUSTOM_CITY_OPTION_VALUE;
    const isCompanyInfoReadOnly = !canEditCompanyInfo;

    const {
        data: infoCompanyData,
        loading: loadingCompany,
        refetch: refetchInfoCompany,
    } = useGetInfoCompanyForUpdateCompanyPageQuery({
        variables: { companyId: +currentCompanyId },
        skip: !currentCompanyId,
        context: {
            errorType: "global",
        },
        onError: (error: ApolloError) => {
            setIsErrorModalOpen(ErrorService.errorHandling(error));
        },
    });

    const infoCompany = infoCompanyData && infoCompanyData?.user?.company;

    const { data: relationsData, loading: loadingRelations } = useGetRelationsQuery({
        context: {
            errorType: "global",
        },
        variables: { isMessageOnly: true },
        skip: !currentCompanyId,
        onError: (error: ApolloError) => {
            setIsErrorModalOpen(ErrorService.errorHandling(error));
        },
    });

    const [updateCompany] = useUpdateCompanyMutation();

    const handleModalDeleteClose = () => setShowModalDelete(false);
    const handleRecoveryCansel = () => {
        setShowRecoveryModal(false);
    };

    const createInput = (isDeleted: boolean) => {
        const category = parseBusinessType(infoCompany?.category?.type) ?? BUSINESS_TYPE.OTHER;

        return {
            name: infoCompany?.name,
            country: infoCompany?.country?.id,
            address: infoCompany?.address,
            phone: infoCompany?.phone,
            timezone: infoCompany?.timeZone?.id,
            messageLanguage: infoCompany?.messageLanguage?.id,
            currency: infoCompany?.currency?.id,
            deleted: isDeleted,
            category,
        };
    };

    const handleDelete = async () => {
        if (!canDeleteRestoreCompany) return;

        const id = +currentCompanyId;
        const input = createInput(true);
        await updateCompany({
            variables: { id, input, branchId: +branchId },
            context: {
                errorType: "local",
            },
            onError: (error: ApolloError) => {
                setIsErrorModalOpen(ErrorService.errorHandling(error));
            },
        });
        window.location.href = `${process.env.REACT_APP_POS_URL}/logout`;
    };

    const handleRecoveryCompany = async () => {
        if (!canDeleteRestoreCompany) return;

        const id = +currentCompanyId;
        const input = createInput(false);
        await updateCompany({
            variables: { id, input, branchId: +branchId },
            context: {
                errorType: "local",
            },
            onCompleted: (data) => {
                data && refetchInfoCompany();
                data && setShowRecoveryModal(false);
            },
            onError: (error: ApolloError) => {
                setIsErrorModalOpen(ErrorService.errorHandling(error));
            },
        });
    };
    useEffect(() => {
        if (infoCompany) {
            infoCompany.country.id &&
                setSelectedCountry({
                    value: infoCompany?.country?.id,
                    label: infoCompany?.country?.name,
                });
            infoCompany?.city?.id &&
                setSelectedCity({
                    value: infoCompany?.city?.id,
                    label: infoCompany?.city?.name,
                });
            infoCompany?.country?.currencies[0]?.id;
            infoCompany?.country?.timezone[0]?.id;
            infoCompany?.messageLanguage?.id;
        }
    }, [infoCompany]);

    const countryOptions = countryOptionsList(relationsData);
    const currencyOptions = currencyOptionsList(relationsData);
    const timezoneOptions = timezoneOptionsList(timezoneData?.relations?.timezones);

    const selectedCityEntity =
        selectedCity && selectedCity.value !== CUSTOM_CITY_OPTION_VALUE
            ? {
                  id: Number(selectedCity.value),
                  name: selectedCity.label,
              }
            : null;

    const {
        cityOptions,
        isCitiesInitialLoading,
        isCitiesSearchLoading,
        onCityMenuOpen,
        onCityMenuClose,
        onCitySearchChange,
        onCityMenuScrollToBottom,
        resetCities,
    } = useCountryCitiesPaginatedOptions({
        countryId: selectedCountry?.value,
        selectedCity: selectedCityEntity,
        includeCustomCityOption: true,
        customCityOptionLabel: t("Other city"),
        skip: !selectedCountry?.value,
    });

    const handleCountryChange = async (selectedOption: Option, setFieldValue: any) => {
        setSelectedCountry(selectedOption);
        setFieldValue("country", selectedOption?.value);
        setSelectedCity(null);
        setFieldValue("city", null);
        setFieldValue("otherCity", null);
        resetCities();

        const currency = relationsData?.relations?.currencies?.find((currency) =>
            currency?.countries?.some((c) => c.id === selectedOption?.value)
        );

        if (currency) {
            const newCurrency = {
                value: currency.id,
                label: `${currency.code} - ${currency.currency}`,
            };

            setFieldValue("currency", newCurrency.value);
        }
        const country = relationsData?.relations?.countries?.find(({ id }) => id === selectedOption?.value);

        if (country?.timezone?.length) {
            setFieldValue("timezone", country.timezone[0].id);
        }
    };

    const { data: languageVersionsData, loading: languageVersionsLoading } = useGetLanguagesForLanguageVersionsQuery({
        context: {
            errorType: "local",
        },
        onError: (error: ApolloError) => {
            console.error("error", error);
        },
    });

    useEffect(() => {
        if (navState.focus !== "menu_languages" || languageVersionsLoading) return;

        const target = document.getElementById("editCompany-languages-field");
        if (target) {
            target.scrollIntoView({ behavior: "smooth", block: "start" });
        }
    }, [languageVersionsLoading, navState.focus]);

    const languageVersionOptions: Option[] = getLanguageVersionOptions(languageVersionsData?.relations?.languages);

    const handleCityChange = (selectedOption: Option, setFieldValue: any) => {
        setSelectedCity(selectedOption);

        if (selectedOption?.value !== CUSTOM_CITY_OPTION_VALUE) {
            setFieldValue("otherCity", null);
        } else {
            setFieldValue("city", null);
        }
        setFieldValue("city", selectedOption ? selectedOption.value : null);
    };

    const handleCurrencyChange = (selectedOption: Option, setFieldValue: any) => {
        setFieldValue("currency", selectedOption ? selectedOption.value : "");
    };

    useEffect(() => {
        const company = infoCompanyData?.user?.company;
        if (!company) return;

        const nextInitialValues: CompanyInput = {
            name: company.name ?? "",
            address: company.address ?? "",
            phone: company.phone ?? "",
            website: company.website ?? null,
            country: company.country?.id ?? null,
            city: company.city?.id ?? null,
            otherCity: null,
            currency: company.currency?.id ?? null,
            timezone: company.timeZone?.id ?? null,
            messageLanguage: company.messageLanguage?.id ?? null,
            logo: company.image ?? null,
            deleted: company.deleted ?? false,
            category: parseBusinessType(company.category?.type) ?? BUSINESS_TYPE.OTHER,
            languages: company.languages?.map((language) => language.id) ?? [],
            primaryLanguage: company.primaryLanguage?.id ?? null,
        };

        setInitialValues(nextInitialValues);
    }, [infoCompanyData]);
    const handleUpdateSubmit = async (
        values: CompanyInput,
        { setSubmitting, setFieldError }: FormikHelpers<CompanyInput>
    ) => {
        if (!canEditCompanyInfo) {
            setSubmitting(false);
            return;
        }

        const id = +currentCompanyId;
        const category = parseBusinessType(infoCompany?.category?.type) ?? BUSINESS_TYPE.OTHER;
        const previousPrimaryLanguageId = enabledMenuModule ? (infoCompany?.primaryLanguage?.id ?? null) : null;
        const nextPrimaryLanguageId = enabledMenuModule ? (values.primaryLanguage ?? null) : null;
        const selectedCityId = values.city ? Number(values.city) : null;

        const input: CompanyInput = {
            name: values?.name,
            country: values?.country,
            city: isOtherCity ? null : selectedCityId,
            otherCity: values?.otherCity ? values?.otherCity : null,
            address: values?.address,
            phone: values?.phone,
            website: values?.website,
            timezone: values?.timezone,
            messageLanguage: values?.messageLanguage,
            currency: values?.currency,
            isUpdateLogo: values?.isUpdateLogo || false,
            deleted: false,
            category,
        };
        if (enabledMenuModule) {
            input.languages = values.languages ?? [];
            input.primaryLanguage = values.primaryLanguage !== undefined ? values.primaryLanguage : null;
        }
        if (values?.isUpdateLogo === true) input.logo = values.logo;

        await updateCompany({
            variables: { id, input, branchId: +branchId },
            context: {
                errorType: "local",
            },
            onCompleted: (data) => {
                data && refetchInfoCompany();
                currencySingVar(data.updateCompany.currency.currencySign);
                void resetMenuLanguageVersionOnPrimaryChange({
                    previousPrimaryLanguageId,
                    nextPrimaryLanguageId,
                    resolveNextPrimaryIsoCode: (primaryLanguageId) =>
                        relationsData?.relations?.languages?.find((language) => language?.id === primaryLanguageId)
                            ?.isoCode || null,
                });
                const toastState = { text: t("Saved"), toast: true };
                if (returnTo) {
                    navigate(returnTo, { replace: true, state: toastState });
                } else {
                    navigate(`/branches/main/branch/${branchId}`, { state: toastState });
                }
                setSubmitting(false);
            },
            onError: (error: ApolloError) => {
                const validationErrors = (error.graphQLErrors[0].extensions as unknown as ValidationErrorExtensions)
                    .validation;
                const websiteErrors = validationErrors.website;
                if (Array.isArray(websiteErrors)) {
                    const longError = websiteErrors.find((e) =>
                        e.message.includes("This value is too long. It should have 2040 characters or less.")
                    );
                    if (longError) {
                        setFieldError("website", t("The value length for this field must not exceed 2040 characters."));
                    }
                }
                setIsErrorModalOpen(ErrorService.errorHandling(error));
                setSubmitting(false);
            },
        });
    };
    const handlePhoto = (setFieldValue: SetFieldValueType) => {
        setFieldValue("isUpdateLogo", true);
    };
    if (loadingCompany || loadingRelations || !currentCompanyId || languageVersionsLoading || !initialValues)
        return <Spinner />;
    if (!canViewCompanyInfo) return null;

    return (
        <>
            <Formik
                initialValues={initialValues}
                validationSchema={updateCompanyFormValidationSchema(t, enabledMenuModule)}
                onSubmit={(values, actions) => handleUpdateSubmit(values, actions)}
                enableReinitialize
            >
                {({ setFieldValue, values, initialValues, isSubmitting, submitCount, errors }) => {
                    const selectedLanguageIds = values.languages ?? [];

                    const primaryLanguageOptions: Option[] = (languageVersionOptions ?? []).filter((option) =>
                        selectedLanguageIds.includes(option.value)
                    );

                    const primaryLanguageValue =
                        primaryLanguageOptions.find((opt) => opt.value === values.primaryLanguage) || null;

                    return (
                        <Form>
                            <ScrollToErrorHelper errors={errors} submitClickedCount={submitCount} />
                            <Row className="container-max-widths-xxl flex-column flex-lg-row mt-3">
                                <Col lg={9} className="pe-lg-5">
                                    <h4 className="mb-4">{t("basicInfo")}</h4>
                                    <div className="pe-lg-4">
                                        <Row className="mb-3 mb-sm-4">
                                            <TextInput
                                                id="editCompany-name-field"
                                                name="name"
                                                label={<span className="required-label">{t("Company name")}</span>}
                                                placeholder={t("Enter your company name")}
                                                textTooltip={t(
                                                    "These data are displayed in the Online Registration form"
                                                )}
                                                maxLength={50}
                                                disabled={isCompanyInfoReadOnly}
                                            />
                                        </Row>
                                        <Row className="mb-3 mb-sm-4">
                                            <Col
                                                sm={3}
                                                md={2}
                                                className="text-start mb-2 mb-sm-0 d-flex align-items-center"
                                            >
                                                <label htmlFor="country" className="required-label">
                                                    <span className="fs-7 fw-bold text-start">{t("country")}</span>
                                                </label>
                                            </Col>
                                            <Col sm={9} md={10}>
                                                <ReactSelect
                                                    id="editCompany-country-field"
                                                    name="country"
                                                    inputAutoComplete="new-password"
                                                    value={selectedCountry}
                                                    options={countryOptions}
                                                    placeholder={t("Choose a country")}
                                                    onChange={(option) => handleCountryChange(option, setFieldValue)}
                                                    isSearchable={true}
                                                    disabled={isCompanyInfoReadOnly}
                                                />
                                            </Col>
                                        </Row>
                                        <Row className="mb-3 mb-sm-4">
                                            <Col
                                                sm={3}
                                                md={2}
                                                className="text-start mb-2 mb-sm-0 d-flex align-items-center"
                                            >
                                                <label htmlFor="city" className="required-label">
                                                    <span className="fs-7 fw-bold text-start">{t("city")}</span>
                                                </label>
                                            </Col>
                                            <Col sm={9} md={10}>
                                                <ReactAsyncSelect
                                                    id="editCompany-city-field"
                                                    name="city"
                                                    inputAutoComplete="new-password"
                                                    value={selectedCity}
                                                    options={cityOptions}
                                                    placeholder={t("Choose a city")}
                                                    onChange={(option) => handleCityChange(option, setFieldValue)}
                                                    isSearchable
                                                    isLoading={isCitiesInitialLoading || isCitiesSearchLoading}
                                                    onMenuOpen={onCityMenuOpen}
                                                    onMenuClose={onCityMenuClose}
                                                    onInputChange={onCitySearchChange}
                                                    onMenuScrollToBottom={onCityMenuScrollToBottom}
                                                    disableLocalFilter
                                                    noOptionsMessage={() => t("No options")}
                                                    loadingMessage={() => "..."}
                                                    disabled={isCompanyInfoReadOnly}
                                                />
                                            </Col>
                                        </Row>
                                        {isOtherCity && (
                                            <Row className="mb-3 mb-sm-4">
                                                <TextInput
                                                    id="editCompany-otherCity-field"
                                                    name="otherCity"
                                                    autoComplete="new-password"
                                                    label={<span>{t("Add city name")}</span>}
                                                    placeholder={t("")}
                                                    maxLength={50}
                                                    required
                                                    disabled={isCompanyInfoReadOnly}
                                                />
                                            </Row>
                                        )}
                                        <Row className="mb-3 mb-sm-4">
                                            <TextInput
                                                id="editCompany-address-field"
                                                name="address"
                                                autoComplete="address-line1"
                                                label={<span>{t("address")}</span>}
                                                placeholder={t("Enter your location")}
                                                maxLength={200}
                                                required
                                                disabled={isCompanyInfoReadOnly}
                                            />
                                        </Row>
                                        <Row className="mb-3 mb-sm-4">
                                            <Col sm={3} md={2} className="mb-2 mb-sm-0 d-flex align-items-center">
                                                <label
                                                    htmlFor="editCompany-phone-field"
                                                    className="text-dark fs-7 fw-bold required-label"
                                                    onClick={() => phonePickerRef.current?.focus()}
                                                >
                                                    {t("phone")}
                                                </label>
                                            </Col>
                                            <Col sm={9} md={10}>
                                                <PhonePicker
                                                    id="editCompany-phone-field"
                                                    phoneRef={phonePickerRef}
                                                    name="phone"
                                                    value={infoCompany?.phone ? infoCompany?.phone : ""}
                                                    disabled={isCompanyInfoReadOnly}
                                                />
                                                <ErrorMessage name="phone">
                                                    {(msg) => <div className="invalid-feedback d-block">{msg}</div>}
                                                </ErrorMessage>
                                            </Col>
                                        </Row>
                                        <Row className="mb-3 mb-sm-4">
                                            <TextInput
                                                id="editCompany-website-field"
                                                name="website"
                                                label={t("website")}
                                                placeholder={t("Site address")}
                                                maxLength={2040}
                                                disabled={isCompanyInfoReadOnly}
                                            />
                                        </Row>
                                        <Row className="mb-3 mb-sm-4">
                                            <Col
                                                sm={3}
                                                md={2}
                                                className="text-start mb-2 mb-sm-0 d-flex align-items-center"
                                            >
                                                <label htmlFor="timezone" className="required-label">
                                                    <span className="fs-7 fw-bold text-start">{t("Time zone")}</span>
                                                </label>
                                            </Col>
                                            <Col sm={9} md={10}>
                                                <ReactSelect
                                                    id="editCompany-timezone-field"
                                                    name="timezone"
                                                    options={timezoneOptions}
                                                    value={timezoneOptions?.find(
                                                        (option: any) => option.value === values.timezone
                                                    )}
                                                    onChange={(selectedOption: any) => {
                                                        setFieldValue(
                                                            "timezone",
                                                            selectedOption ? selectedOption.value : ""
                                                        );
                                                    }}
                                                    placeholder={t("Choose a time zone")}
                                                    isSearchable={false}
                                                    disabled={isCompanyInfoReadOnly || !timezoneOptions?.length}
                                                />
                                            </Col>
                                        </Row>
                                        <Row className="mb-3 mb-sm-4">
                                            <Col
                                                sm={3}
                                                md={2}
                                                className="text-start mb-2 mb-sm-0 d-flex align-items-center"
                                            >
                                                <label htmlFor="messageLanguage" className="required-label">
                                                    <span className="fs-7 fw-bold text-start">
                                                        {t("Language Messages")}
                                                    </span>
                                                </label>
                                            </Col>
                                            <Col sm={9} md={10}>
                                                <ReactSelect
                                                    id="editCompany-messageLanguage-field"
                                                    name="messageLanguage"
                                                    value={LanguagesService.languageFilter(
                                                        relationsData?.relations?.languages
                                                    ).find((option: any) => option.value === values.messageLanguage)}
                                                    options={LanguagesService.languageFilter(
                                                        relationsData?.relations?.languages
                                                    )}
                                                    placeholder={t("Choose a language")}
                                                    onChange={(selectedOption: any) => {
                                                        setFieldValue(
                                                            "messageLanguage",
                                                            selectedOption
                                                                ? selectedOption.value
                                                                : initialValues.messageLanguage
                                                        );
                                                    }}
                                                    isSearchable={true}
                                                    disabled={isCompanyInfoReadOnly}
                                                />
                                            </Col>
                                        </Row>
                                        <WithModulesAccess modules={[AllModulesTagName.MENU]}>
                                            <>
                                                <Row className="mb-4">
                                                    <Col sm={3} md={2} className="text-start mb-2 mb-sm-0">
                                                        <label
                                                            htmlFor="editCompany-languages-field"
                                                            className="required-label"
                                                        >
                                                            <span className="fs-7 fw-bold text-start">
                                                                {t("Language versions of the menu")}
                                                            </span>
                                                        </label>
                                                    </Col>
                                                    <Col sm={9} md={10}>
                                                        <MultiSelect
                                                            id="editCompany-languages-field"
                                                            name="languages"
                                                            placeholder={t("Select additional menu languages")}
                                                            options={languageVersionOptions ?? []}
                                                            value={languageVersionOptions.filter((option) =>
                                                                (selectedLanguageIds ?? []).includes(option.value)
                                                            )}
                                                            onChange={(selectedOptions: MultiOption[] | null) => {
                                                                const newIds = (selectedOptions ?? []).map(
                                                                    (opt) => opt.value
                                                                );
                                                                setFieldValue("languages", newIds);
                                                                if (
                                                                    values.primaryLanguage != null &&
                                                                    !newIds.includes(values.primaryLanguage)
                                                                ) {
                                                                    setFieldValue("primaryLanguage", null);
                                                                }
                                                            }}
                                                            isSearchable
                                                            disabled={isCompanyInfoReadOnly}
                                                        />
                                                        <ErrorMessage name="languages">
                                                            {(msg) => (
                                                                <div className="invalid-feedback d-block">{msg}</div>
                                                            )}
                                                        </ErrorMessage>
                                                    </Col>
                                                </Row>
                                                <Row className="mb-4">
                                                    <Col sm={3} md={2} className="text-start mb-2 mb-sm-0">
                                                        <label htmlFor="primaryLanguage" className="required-label">
                                                            <span className="fs-7 fw-bold text-start">
                                                                {t("Main language menu")}
                                                            </span>
                                                        </label>
                                                    </Col>
                                                    <Col sm={9} md={10}>
                                                        <ReactSelect
                                                            id="editCompany-primaryLanguage-field"
                                                            name="primaryLanguage"
                                                            value={primaryLanguageValue}
                                                            options={primaryLanguageOptions}
                                                            placeholder={t("Choose a language")}
                                                            onChange={(option: Option | null) =>
                                                                setFieldValue(
                                                                    "primaryLanguage",
                                                                    option ? option.value : null
                                                                )
                                                            }
                                                            isSearchable={true}
                                                            disabled={isCompanyInfoReadOnly || !primaryLanguageOptions?.length}
                                                        />
                                                        <ErrorMessage name="primaryLanguage">
                                                            {(msg) => (
                                                                <div className="invalid-feedback d-block">{msg}</div>
                                                            )}
                                                        </ErrorMessage>
                                                    </Col>
                                                </Row>
                                            </>
                                        </WithModulesAccess>
                                        <Row className="mb-3 mb-sm-4">
                                            <Col
                                                sm={3}
                                                md={2}
                                                className="text-start mb-2 mb-sm-0 d-flex align-items-center"
                                            >
                                                <label htmlFor="currency" className="required-label">
                                                    <span className="fs-7 fw-bold text-start">{t("Currency")}</span>
                                                </label>
                                            </Col>
                                            <Col sm={9} md={10}>
                                                <ReactSelect
                                                    id="editCompany-currency-field"
                                                    name="currency"
                                                    value={currencyOptions.find(
                                                        (option: any) => option.value === values.currency
                                                    )}
                                                    options={currencyOptions}
                                                    placeholder={t("Choose a currency")}
                                                    onChange={(selectedOption) =>
                                                        handleCurrencyChange(selectedOption, setFieldValue)
                                                    }
                                                    isSearchable={true}
                                                    disabled={isCompanyInfoReadOnly}
                                                />
                                            </Col>
                                        </Row>
                                    </div>
                                </Col>
                                <Col lg={3} className="ps-lg-0">
                                    <div className="d-flex mb-3 w-100 flex-column  align-items-lg-end justify-content-lg-end">
                                        <h4 className="mb-4 w-100" style={{ maxWidth: "325px" }}>
                                            {t("Logo")}
                                        </h4>
                                        <ImageCrop
                                            previewPhoto={infoCompany?.image}
                                            setFieldValue={setFieldValue}
                                            fieldSetter={() => handlePhoto(setFieldValue)}
                                            fieldName="logo"
                                            dismissImage={() => {
                                                setFieldValue("isUpdateLogo", true);
                                                setFieldValue("logo", null);
                                            }}
                                            modalTitle={t("Logo")}
                                            disabled={isCompanyInfoReadOnly}
                                        />
                                        <ErrorMessage name="logo">{(msg) => <div>{msg}</div>}</ErrorMessage>
                                    </div>
                                </Col>
                            </Row>
                            <Row className="mt-2 mt-lg-5">
                                <Col lg={4} className="d-sm-flex gap-4">
                                    <Button
                                        variant="outline-primary"
                                        type="button"
                                        className="custom-btn mb-3 mb-sm-0"
                                        onClick={() => {
                                            if (returnTo) {
                                                navigate(returnTo, { replace: true });
                                                return;
                                            }
                                            navigate(`/branches/main/branch/${branchId}`);
                                        }}
                                    >
                                        {t("cancel")}
                                    </Button>
                                    {canEditCompanyInfo ? (
                                        <Button
                                            variant="primary"
                                            type="submit"
                                            className="custom-btn"
                                            disabled={isSubmitting || !canEditCompanyInfo}
                                        >
                                            {isSubmitting ? (
                                                <SpinnerLoader variant="light" loadingText={t("Saving...")} />
                                            ) : (
                                                t("save")
                                            )}
                                        </Button>
                                    ) : null}
                                </Col>
                            </Row>
                        </Form>
                    );
                }}
            </Formik>
            <ModalDelete
                show={showModalDelete && canDeleteRestoreCompany}
                handleClose={handleModalDeleteClose}
                title={t("Company Deletion")}
                body={`${t("Are you sure you want to delete the company and all related information? Once deleted, the company cannot be restored.")}`}
                buttons={[
                    {
                        text: t("delete"),
                        onClick: handleDelete,
                        variant: "btn btn-outline-violet border-primary text-primary fw-normal",
                        className: "d-flex justify-content-center align-items-center w-100",
                        disabled: !canDeleteRestoreCompany,
                    },
                    {
                        text: t("cancel"),
                        onClick: handleModalDeleteClose,
                        variant: "primary",
                        className: "w-100",
                    },
                ]}
            />
            <ModalDelete
                show={showRecoveryModal && canDeleteRestoreCompany}
                handleClose={handleRecoveryModalClose}
                title={t("Your Company Deleted")}
                body={`${t("Your company has been removed.\nRestore data?")}`}
                buttons={[
                    {
                        text: t("cancel"),
                        onClick: handleRecoveryCansel,
                        variant: "primary",
                        className: "w-100",
                    },
                    {
                        text: t("Restore"),
                        onClick: handleRecoveryCompany,
                        variant: "btn btn-outline-violet border-primary text-primary fw-normal",
                        className: "d-flex justify-content-center align-items-center w-100",
                        disabled: !canDeleteRestoreCompany,
                    },
                ]}
            />
            {isErrorModalOpen ? (
                <ErrorModalComponent
                    i18n={i18next}
                    onClose={() => {
                        setIsErrorModalOpen(null);
                    }}
                    isOpen={!!isErrorModalOpen}
                    currentError={isErrorModalOpen}
                />
            ) : null}
        </>
    );
};

export default UpdateCompanyForm;
